import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { CalendarDays, ClipboardList, Music4, Gavel, Trophy, Newspaper, Medal, LogOut, Menu, X, FolderOpen } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import EventsSection from "@/components/EventsSection";
import AthletesSection from "@/components/AthletesSection";
import ProgramSection from "@/components/ProgramSection";
import JudgesSection from "@/components/JudgesSection";
import RankingsSection from "@/components/RankingsSection";
import GroupsManager from "@/components/GroupsManager";
import NewsSection from "@/components/NewsSection";
import DocumentsSection from "@/components/DocumentsSection";

const roleLabels = { admin: "Amministratore", judge: "Giudice", segreteria: "Segreteria", public: "Utente" };

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { id: "calendario", label: "Calendario", icon: CalendarDays, roles: ["admin", "segreteria", "public"] },
    { id: "notizie", label: "Notizie", icon: Newspaper, roles: ["admin", "segreteria", "public"] },
    { id: "iscrizioni", label: "Iscrizioni Atleti", icon: ClipboardList, roles: ["admin", "segreteria", "public"] },
    { id: "programma", label: "Programma di Lavoro", icon: Music4, roles: ["admin", "segreteria", "public"] },
    { id: "giudici", label: "Area Giudici", icon: Gavel, roles: ["admin", "judge", "segreteria", "direttore"] },
    { id: "classifica", label: "Classifica", icon: Trophy, roles: ["admin", "segreteria", "direttore", "judge", "public"] },
    { id: "documenti", label: "Documenti", icon: FolderOpen, roles: ["admin"] },
  ].filter((n) => n.roles.includes(user?.role));

  const [section, setSection] = useState(() => {
    const requested = searchParams.get("section");
    if (requested && nav.some((n) => n.id === requested)) return requested;
    return user?.role === "judge" ? "giudici" : "calendario";
  });

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex noise-bg">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#0F172A] border-r border-white/10 flex flex-col transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-6 border-b border-white/10">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <SiteLogo className="w-9 h-9" />
            <div className="flex flex-col leading-tight">
              <BrandText />
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => { setSection(n.id); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${section === n.id ? "bg-gold/10 text-gold" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="px-2 mb-3">
            <p className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs text-gold">{roleLabels[user?.role] || user?.role}</p>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-16 border-b border-white/10 flex items-center justify-between px-6 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h1 className="font-display text-2xl tracking-tight capitalize">{nav.find((n) => n.id === section)?.label}</h1>
          </div>
        </header>
        <div className="p-6 max-w-6xl mx-auto">
          {!nav.some((n) => n.id === section) ? (
            <div data-testid="section-access-denied" className="rounded-xl border border-white/10 bg-[#0F172A] p-10 text-center">
              <p className="text-lg font-semibold">Accesso non consentito</p>
              <p className="text-muted-foreground text-sm mt-1">Non hai i permessi per accedere a questa sezione.</p>
            </div>
          ) : (
            <>
              {section === "calendario" && <EventsSection />}
              {section === "notizie" && <NewsSection />}
              {section === "iscrizioni" && (<div className="space-y-10"><AthletesSection /><GroupsManager /></div>)}
              {section === "programma" && <ProgramSection />}
              {section === "giudici" && <JudgesSection />}
              {section === "classifica" && <RankingsSection />}
              {section === "documenti" && <DocumentsSection />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
